import { Info, Loader2, Mail, Star, Truck } from "lucide-react";
import { cn } from "@/lib/cn";
import { useSupplierSuggestion } from "@/hooks/useSuppliers";
import type { SafeSupplier } from "@/api/suppliers";

/**
 * D-133 Фаза A (A-W2 + A-W3) — READ-ONLY менеджерський супровід постачальника.
 *
 * Показує авто-пропозицію постачальника по бренду рекламації (default ★) +
 * активних кандидатів — це менеджерський пікер у read-only режимі (active-only,
 * 🔴 БЕЗ notes: бекенд віддає лише safe-поля). Дій немає — відправка у фазі C.
 */

const ROLE_LABEL: Record<string, string> = {
  manufacturer: "виробник",
  distributor: "дистрибʼютор",
  importer: "імпортер",
};
const LANG_LABEL: Record<string, string> = { uk: "укр", en: "англ" };

function roleForBrand(s: SafeSupplier, biBrandId: string | null): string | null {
  if (!biBrandId) return null;
  const m = s.brands.find((b) => b.biBrandId === biBrandId);
  return m ? ROLE_LABEL[m.role] ?? m.role : null;
}

function SupplierRow({
  s,
  biBrandId,
  isDefault,
}: {
  s: SafeSupplier;
  biBrandId: string | null;
  isDefault: boolean;
}) {
  const role = roleForBrand(s, biBrandId);
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2",
        isDefault ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "bg-muted/20",
      )}
    >
      <div className="flex items-center gap-1.5 text-sm font-medium">
        {isDefault && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" aria-hidden />}
        <span className="truncate">{s.name}</span>
        {role && (
          <span className="ml-auto rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {role}
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        {s.email && (
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3 w-3" aria-hidden /> {s.email}
          </span>
        )}
        <span>мова: {LANG_LABEL[s.language] ?? s.language}</span>
        {s.qualityContactEmail && <span>якість: {s.qualityContactEmail}</span>}
      </div>
    </div>
  );
}

export function SupplierSuggestionCard({ complaintId }: { complaintId: string }) {
  const q = useSupplierSuggestion(complaintId);

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Підбір постачальника…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        Не вдалося підібрати постачальника.
      </div>
    );
  }

  const { brand, default: def, suppliers, reason } = q.data;
  const biBrandId = brand.biBrandId;
  const isManual = reason === "no_brand_manual" || reason === "no_mapping_manual" || reason === "no_brand";
  const rest = suppliers.filter((s) => s.id !== def?.id);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Truck className="h-3.5 w-3.5" aria-hidden />
        <span>
          Бренд: <span className="font-medium text-foreground">{brand.display}</span>
        </span>
      </div>

      {def && <SupplierRow s={def} biBrandId={biBrandId} isDefault />}

      {rest.length > 0 && (
        <div className="space-y-1.5">
          {def && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Інші активні постачальники
            </p>
          )}
          {rest.map((s) => (
            <SupplierRow key={s.id} s={s} biBrandId={biBrandId} isDefault={false} />
          ))}
        </div>
      )}

      {suppliers.length === 0 && (
        <p className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          Активних постачальників для цього бренду немає.
        </p>
      )}

      {isManual && suppliers.length > 0 && (
        <p className="flex items-start gap-1 text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>Авто-пропозиція по бренду недоступна — постачальника обирають вручну.</span>
        </p>
      )}
    </div>
  );
}
